import MUISketelon, { SkeletonOwnProps } from '@mui/material/Skeleton';
import classNames from 'classnames';

import { BorderRadius } from '../Theme';

import { useStyles } from './Skeleton.styles';

enum SkeletonVariants {
  circle = 'circle',
  rect = 'rect',
  text = 'text',
}

enum MUISkeletonVariants {
  circle = 'circular',
  rect = 'rectangular',
  text = 'text',
}

/**
 * Свойства компонента {@link Skeleton}
 */
export interface SkeletonProps {
  /**
   * Вариант skeleton ('circle', 'rect' или 'text').
   *
   * @defaultValue `'text'`
   */
  variant?: keyof typeof SkeletonVariants;

  /**
   * Ширина skeleton.
   */
  width?: number | string;

  /**
   * Высота skeleton.
   */
  height?: number | string;

  /**
   * Радиус скругления углов.
   */
  radius?: keyof BorderRadius;

  /**
   * Анимация
   */
  animation?: SkeletonOwnProps['animation'];
}

/**
 * Компонент Skeleton, который отображает загрузочные заполнители различных форм и размеров.
 *
 * @public
 * @param variant - Вариант сkeleton ('circle', 'rect' или 'text').
 * @param width - Ширина сkeleton.
 * @param height - Высота сkeleton.
 * @param radius - Радиус скругления углов.
 * @param animation - Анимация. ('pulse', 'wave' иди false)
 * @returns Компонент Skeleton.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SkeletonProps}
 */
export const Skeleton = ({
  variant = 'text',
  width,
  height,
  radius,
  animation = 'pulse',
}: SkeletonProps) => {
  const { classes: styles } = useStyles({ radius });

  /**
   * Функция getVariantStyles возвращает стили для компонента Skeleton в зависимости от его варианта.
   *
   * @param skeletonVariant - Вариант сkeleton ('circle', 'rect' или 'text').
   * @returns - CSS-классы для выбранного варианта сkeleton.
   */
  const getVariantStyles = (skeletonVariant: keyof typeof SkeletonVariants) => {
    return styles[`${SkeletonVariants[skeletonVariant]}`];
  };

  /**
   * Функция convertToMUIVariant преобразует пропс 'variant' Skeleton в 'variant' для MUISketelon.
   * @param skeletonVariant - Вариант ckeleton ('circle', 'rect' или 'text').
   * @returns - Вариант для MUISketelon ('circular', 'rectangular' или 'text').
   */
  const convertToMUIVariant = (
    skeletonVariant: keyof typeof SkeletonVariants,
  ): MUISkeletonVariants => {
    return MUISkeletonVariants[skeletonVariant];
  };

  return (
    <MUISketelon
      className={classNames([
        styles.body,
        radius ? styles.radius : getVariantStyles(variant),
      ])}
      variant={convertToMUIVariant(variant)}
      width={width}
      height={height}
      data-testid="skeleton-test-id"
      animation={animation}
    />
  );
};
